/** Agency transfers: papers sent between sheriff's offices (and to offices not on CivilHound by secure link). */
import { useState } from 'react';
import { api, fileToBase64 } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { fmtDate, fmtDateTime } from '../shared/format.ts';
import { Link, useRouter } from '../shared/router.tsx';
import { Badge, Card, Empty, ErrorBox, Field, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';

const STATUS: Record<string, [string, 'ok' | 'warn' | 'bad' | 'info' | 'neutral']> = {
  sent: ['Waiting to be accepted', 'warn'],
  accepted: ['Accepted — being served', 'info'],
  declined: ['Declined', 'bad'],
  returned: ['Return sent back', 'ok'],
  completed: ['Completed', 'neutral'],
  cancelled: ['Taken back', 'neutral'],
};
export const TransferStatus = ({ s }: { s: string }) => <Badge tone={STATUS[s]?.[1] ?? 'neutral'}>{STATUS[s]?.[0] ?? s}</Badge>;
const addr = (a: any) => [a.line1, a.line2, a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');

async function filesPayload(files: File[]) {
  return Promise.all(files.map(async (f) => ({ filename: f.name, dataBase64: await fileToBase64(f) })));
}

/** Copy-able secure link with an email button (until an email service is connected). */
export function LinkBox({ link, to, email, paper }: { link: string; to: string; email?: string | null; paper: string }) {
  const url = `${location.origin}${link}`;
  const [copied, setCopied] = useState(false);
  const subject = encodeURIComponent(`Civil paper for service — ${paper}`);
  const body = encodeURIComponent(`${to}:\n\nWe are sending you a civil paper to serve. Open this secure link to view and print the original and to send us the return when it's done:\n\n${url}\n\nThank you.`);
  return (
    <div className="link-box">
      <div className="small">Secure link for {to} (works for 120 days; no account needed):</div>
      <div className="row gap-sm wrap">
        <input readOnly value={url} aria-label="Secure link" onFocus={(e) => e.target.select()} className="grow mono" />
        <button type="button" className="btn sm" onClick={() => navigator.clipboard?.writeText(url).then(() => setCopied(true))}>
          {copied ? '✓ Copied' : 'Copy link'}
        </button>
        <a className="btn sm primary" href={`mailto:${email ?? ''}?subject=${subject}&body=${body}`}>
          ✉ Email it
        </a>
      </div>
    </div>
  );
}

/** "Send to another county" from a paper. */
export function SendTransferDialog({ paper, onClose, onSent }: { paper: any; onClose: () => void; onSent: () => void }) {
  const ag = useLoad(() => api.get<any>('/api/transfers/agencies'));
  const [to, setTo] = useState('');
  const [extName, setExtName] = useState('');
  const [extEmail, setExtEmail] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [note, setNote] = useState('');
  const [link, setLink] = useState<{ link: string; to: string } | null>(null);
  const a = useAction();
  const external = to === '__ext__';
  const send = () =>
    a.run(async () => {
      const r = await api.post<{ link: string | null; to: string }>(`/api/papers/${paper.id}/transfer`, {
        ...(external ? { external: { name: extName.trim(), email: extEmail.trim() || undefined } } : { receiverAgencyId: to }),
        files: await filesPayload(files),
        note: note.trim() || undefined,
      });
      if (r.link) setLink({ link: r.link, to: r.to });
      else onSent();
    });
  return (
    <div className="modal-back" role="dialog" aria-label="Send to another sheriff's office">
      <div className="modal">
        <h2>Send to another sheriff’s office</h2>
        {link ? (
          <>
            <Notice tone="ok">Sent to {link.to}. This paper is on hold until their return comes back.</Notice>
            <LinkBox link={link.link} to={link.to} email={extEmail} paper={paper.civilos_number} />
            <div className="row gap">
              <button className="btn primary" onClick={onSent}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="small muted">For a defendant in another county. The paper goes on hold here until their return comes back. No fee is charged between sheriff’s offices.</p>
            <Field label="Send to">
              <select value={to} onChange={(e) => setTo(e.target.value)} aria-label="Send to">
                <option value="">Choose…</option>
                {(ag.data?.agencies ?? []).map((x: any) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
                <option value="__ext__">Another office (not on CivilHound) — send a secure link</option>
              </select>
            </Field>
            {external && (
              <div className="grid2">
                <Field label="Office name">
                  <input value={extName} onChange={(e) => setExtName(e.target.value)} placeholder="Lancaster County Sheriff’s Office" />
                </Field>
                <Field label="Their email (optional)">
                  <input type="email" value={extEmail} onChange={(e) => setExtEmail(e.target.value)} placeholder="civil@…" />
                </Field>
              </div>
            )}
            <Field label="Original paper (PDF, required)">
              <input type="file" accept="application/pdf" multiple aria-label="Original paper PDF" onChange={(e) => setFiles([...(e.target.files ?? [])])} />
            </Field>
            <Field label="Note to them (optional)">
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Works nights; try mornings" />
            </Field>
            <ErrorBox error={a.error} />
            <div className="row gap">
              <button className="btn primary" disabled={a.busy || !files.length || !to || (external && extName.trim().length < 3)} onClick={send}>
                {a.busy ? 'Sending…' : 'Send paper'}
              </button>
              <button className="btn ghost" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Card on the paper screen. */
export function PaperTransfersCard({ transfers }: { transfers: any[] }) {
  if (!transfers?.length) return null;
  return (
    <Card title="Other agencies">
      <ul className="plain transfer-mini">
        {transfers.map((t) => (
          <li key={t.id}>
            <span className="grow">
              {t.direction === 'outgoing' ? `Sent to ${t.to}` : `Received from ${t.from}`} · {fmtDate(t.sentAt)} <TransferStatus s={t.status} />
              {t.returnOutcome && <span className="small"> · {t.returnOutcome === 'served' ? 'Served' : 'Not served'}</span>}
            </span>
            <Link to={`/transfers/${t.id}`} className="btn sm">
              Open
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function TransfersPage() {
  const { path } = useRouter();
  const box = new URLSearchParams(path.split('?')[1] ?? '').get('box') === 'outgoing' ? 'outgoing' : 'incoming';
  const r = useLoad(() => api.get<any>(`/api/transfers?box=${box}`), [box]);
  return (
    <div className="stack">
      <div>
        <h1>Agency transfers</h1>
        <p className="muted">Papers other sheriff’s offices sent you to serve, and papers you sent to them. Courts and law offices send papers through the portal; those show up in ScanAI intake.</p>
      </div>
      <div className="tabs">
        <Link to="/transfers?box=incoming" className={box === 'incoming' ? 'active' : ''}>
          Sent to us
        </Link>
        <Link to="/transfers?box=outgoing" className={box === 'outgoing' ? 'active' : ''}>
          We sent
        </Link>
      </div>
      <ErrorBox error={r.error} />
      {!r.data ? (
        <Spinner />
      ) : r.data.transfers.length === 0 ? (
        <Empty>{box === 'incoming' ? 'Nothing sent to you yet.' : 'You haven’t sent any papers to another agency. Use “Send to another county” on a paper.'}</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{box === 'incoming' ? 'From' : 'To'}</th>
                <th>Defendant</th>
                <th>Paper</th>
                <th>Sent</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {r.data.transfers.map((t: any) => (
                <tr key={t.id}>
                  <td>
                    {box === 'incoming' ? t.from : t.to}
                    {t.external && <div className="small muted">secure link</div>}
                  </td>
                  <td>
                    <strong>{t.snapshot.defendant}</strong>
                    <div className="small muted">{addr(t.snapshot.address)}</div>
                  </td>
                  <td className="small">
                    {t.snapshot.paperTypeName}
                    <div className="mono">{t.snapshot.caseNumber}</div>
                  </td>
                  <td className="small">{fmtDateTime(t.sentAt)}</td>
                  <td>
                    <TransferStatus s={t.status} />
                  </td>
                  <td>
                    <Link to={`/transfers/${t.id}`} className="btn sm">
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function TransferDetail({ id }: { id: string }) {
  const { can } = useAuth();
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/transfers/${id}`), [id]);
  const a = useAction();
  const [type, setType] = useState('');
  const [ret, setRet] = useState({ outcome: 'served', date: '', servedTo: '', method: 'personal', notes: '' });
  const [retFiles, setRetFiles] = useState<File[]>([]);
  const [link, setLink] = useState<string | null>(null);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const t = r.data.transfer;
  const s = t.snapshot;
  const incoming = t.direction === 'incoming';
  const typeKnown = r.data.paperTypes.some((x: any) => x.code === s.paperTypeCode);
  const act = (url: string, body?: unknown, after?: (x: any) => void) =>
    a.run(async () => {
      const x = await api.post(url, body ?? {});
      after ? after(x) : r.reload();
    });
  return (
    <div className="stack">
      <Link to={`/transfers?box=${t.direction}`}>← Agency transfers</Link>
      <div className="row between wrap gap">
        <div>
          <h1>
            {incoming ? `From ${t.from}` : `To ${t.to}`} · {s.defendant}
          </h1>
          <div className="muted small">
            Sent {fmtDateTime(t.sentAt)} · their paper <span className="mono">{s.senderPaperNumber}</span> <TransferStatus s={t.status} />
          </div>
        </div>
        {t.myPaperId && (
          <Link to={`/papers/${t.myPaperId}`} className="btn">
            Open our paper
          </Link>
        )}
      </div>
      <ErrorBox error={a.error} />
      <div className="grid2 transfer-grid">
        <Card title="The paper">
          <dl className="kv">
            <dt>Paper</dt>
            <dd>{s.paperTypeName}</dd>
            <dt>Case</dt>
            <dd className="mono">{s.caseNumber}</dd>
            <dt>Plaintiff</dt>
            <dd>{s.plaintiff ?? '—'}</dd>
            <dt>Defendant</dt>
            <dd>
              {s.defendant}
              {s.defendantPhone ? ` · ${s.defendantPhone}` : ''}
            </dd>
            <dt>Address</dt>
            <dd>{addr(s.address)}</dd>
            <dt>Court</dt>
            <dd>{s.court ?? '—'}</dd>
            <dt>Court date</dt>
            <dd>{s.courtDate ? `${fmtDate(s.courtDate)}${s.courtTime ? ` ${s.courtTime}` : ''}` : '—'}</dd>
            {s.ivD && (
              <>
                <dt>IV-D</dt>
                <dd>Yes</dd>
              </>
            )}
            {s.specialInstructions && (
              <>
                <dt>Instructions</dt>
                <dd>{s.specialInstructions}</dd>
              </>
            )}
            {t.note && (
              <>
                <dt>Note</dt>
                <dd>{t.note}</dd>
              </>
            )}
            <dt>Contact</dt>
            <dd>
              {s.senderAgency}
              {s.senderPhone ? ` · ${s.senderPhone}` : ''}
            </dd>
          </dl>
        </Card>
        <Card title="Files">
          <ul className="plain">
            {r.data.files.map((f: any) => (
              <li key={f.id}>
                <a href={`/api/transfers/${id}/files/${f.id}`} target="_blank" rel="noreferrer">
                  📄 {f.filename}
                </a>{' '}
                <span className="small muted">· {f.direction === 'outbound' ? 'original paper' : 'return'}</span>
              </li>
            ))}
          </ul>
          {t.returnOutcome && (
            <Notice tone={t.returnOutcome === 'served' ? 'ok' : 'warn'}>
              {t.returnOutcome === 'served' ? 'Served' : 'Not served'}
              {t.returnDetails?.date ? ` ${fmtDate(t.returnDetails.date)}` : ''}
              {t.returnDetails?.servedTo ? ` to ${t.returnDetails.servedTo}` : ''}
              {t.returnDetails?.method ? ` (${t.returnDetails.method})` : ''}. {t.returnDetails?.notes ?? ''} <span className="small muted">— {t.returnDetails?.by}</span>
            </Notice>
          )}
          {t.declineReason && <Notice tone="bad">Declined: {t.declineReason}</Notice>}
        </Card>
      </div>

      {/* Receiver actions */}
      {incoming && t.status === 'sent' && can('papers.intake') && (
        <Card title="Accept or decline">
          <p className="small muted">Accepting puts it in your intake as a paper from {t.from}, with no fee, and the original attached.</p>
          <div className="row gap wrap">
            {!typeKnown && (
              <Field label={`Which of your paper types is “${s.paperTypeName}”?`}>
                <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Paper type">
                  <option value="">Choose…</option>
                  {r.data.paperTypes.map((x: any) => (
                    <option key={x.code} value={x.code}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <button className="btn primary" disabled={a.busy || (!typeKnown && !type)} onClick={() => act(`/api/transfers/${id}/accept`, { paperTypeCode: type || undefined }, (x) => navigate(`/papers/${x.paperId}`))}>
              Accept and add to intake
            </button>
            <button
              className="btn"
              disabled={a.busy}
              onClick={() => {
                const reason = window.prompt('Why are you declining it? (they’ll see this)')?.trim();
                if (reason) act(`/api/transfers/${id}/decline`, { reason });
              }}
            >
              Decline
            </button>
          </div>
        </Card>
      )}
      {incoming && t.status === 'accepted' && can('papers.review') && (
        <Card title={`Send the return to ${t.from}`}>
          <div className="grid2">
            <Field label="Result">
              <select value={ret.outcome} onChange={(e) => setRet({ ...ret, outcome: e.target.value })}>
                <option value="served">Served</option>
                <option value="non_served">Not served</option>
              </select>
            </Field>
            <Field label="Date">
              <input type="date" value={ret.date} onChange={(e) => setRet({ ...ret, date: e.target.value })} />
            </Field>
            {ret.outcome === 'served' && (
              <>
                <Field label="Served to">
                  <input value={ret.servedTo} onChange={(e) => setRet({ ...ret, servedTo: e.target.value })} />
                </Field>
                <Field label="How">
                  <select value={ret.method} onChange={(e) => setRet({ ...ret, method: e.target.value })}>
                    <option value="personal">Personal</option>
                    <option value="substitute">Substitute</option>
                    <option value="posting">Posting</option>
                  </select>
                </Field>
              </>
            )}
          </div>
          <Field label="Notes">
            <textarea rows={2} value={ret.notes} onChange={(e) => setRet({ ...ret, notes: e.target.value })} />
          </Field>
          <Field label="Return / affidavit (PDF)" hint="Leave empty to send the signed affidavit from your paper.">
            <input type="file" accept="application/pdf" multiple aria-label="Return PDF" onChange={(e) => setRetFiles([...(e.target.files ?? [])])} />
          </Field>
          <button
            className="btn primary"
            disabled={a.busy}
            onClick={() => a.run(async () => (await api.post(`/api/transfers/${id}/return`, { ...ret, date: ret.date || undefined, files: retFiles.length ? await filesPayload(retFiles) : undefined }), r.reload()))}
          >
            Send return
          </button>
        </Card>
      )}

      {/* Sender actions */}
      {!incoming && t.status === 'returned' && can('papers.review') && (
        <Notice tone="ok">
          The return is back. Completing attaches it to our paper and marks the paper Returned.{' '}
          <button className="btn sm primary" disabled={a.busy} onClick={() => act(`/api/transfers/${id}/complete`)}>
            Complete
          </button>
        </Notice>
      )}
      {!incoming && ['sent', 'declined'].includes(t.status) && can('papers.review') && (
        <div className="row gap wrap">
          <button className="btn" disabled={a.busy} onClick={() => window.confirm('Take this paper back? It comes off hold here.') && act(`/api/transfers/${id}/cancel`)}>
            Take it back
          </button>
          {t.external && (
            <button className="btn" disabled={a.busy} onClick={() => act(`/api/transfers/${id}/new-link`, {}, (x) => setLink(x.link))}>
              New secure link
            </button>
          )}
        </div>
      )}
      {!incoming && t.external && t.status === 'accepted' && (
        <button className="btn" disabled={a.busy} onClick={() => act(`/api/transfers/${id}/new-link`, {}, (x) => setLink(x.link))}>
          New secure link
        </button>
      )}
      {link && <LinkBox link={link} to={t.to} email={t.externalEmail} paper={s.senderPaperNumber} />}
    </div>
  );
}
